import axios from 'axios';
import logger from '@/utils/logger';

const state = {
    token: localStorage.getItem('token') || null,
    user: JSON.parse(localStorage.getItem('user')) || null,
    isLoggedIn: !!localStorage.getItem('token')
};

const getters = {
    isLoggedIn: state => state.isLoggedIn,
    currentUser: state => state.user,
    token: state => state.token
};

const actions = {
    async login({ commit }, credentials) {
        try {
            const response = await axios.post('/client/login', credentials);
            const { token, client } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(client));

            // Note: Axios interceptor will automatically use token for client routes

            commit('SET_AUTH_SUCCESS', { token, user: client });
            return response;
        } catch (error) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Preserve the original error structure if it has isPendingApproval flag
            // This allows the Login component to show the ApprovalPendingModal
            if (error.response?.data?.isPendingApproval === true) {
                // Re-throw the original error to preserve the response structure
                throw error;
            }
            let message = 'An error occurred. Please try again.';
            if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.message) {
                message = error.message;
            }
            throw new Error(message);
        }
    },

    async signup({ commit }, userData) {
        try {
            // Map companyUrl to companyWebsite for backend
            const payload = { ...userData, companyWebsite: userData.companyUrl };
            delete payload.companyUrl;
            const response = await axios.post('/client/signup', payload);

            if (response.data.isPendingApproval === true) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                commit('LOGOUT');
                return response;
            }

            // Auto-login after successful signup
            if (response.data.token && response.data.client) {
                const { token, client } = response.data;
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(client));
                commit('SET_AUTH_SUCCESS', { token, user: client });
            }
            return response;
        } catch (error) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            let message = 'An error occurred. Please try again.';
            if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.message) {
                message = error.message;
            }
            const err = new Error(message);
            err.code = error.response?.data?.code;
            throw err;
        }
    },

    async logout({ commit, state, rootState }) {
        try {
            const token = state.token || localStorage.getItem('token');
            if (token) {
                let companyId = null;
                try {
                    const fromRoute = (window?.location?.pathname || '').match(/\/company\/(\d+)/i);
                    if (fromRoute && fromRoute[1]) companyId = Number(fromRoute[1]);
                    if (!companyId && rootState?.company?.currentCompany?.id) {
                        companyId = Number(rootState.company.currentCompany.id);
                    }
                    if (!companyId) {
                        const stored = localStorage.getItem('selectedCompanyId') || localStorage.getItem('companyId');
                        if (stored) companyId = Number(stored);
                    }
                } catch (_) { /* best effort */ }
                logger.info('[auth.logout] calling backend /client/logout', { companyId });
                await axios.post('/client/logout', { companyId: companyId || undefined }, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
        } catch (error) {
            logger.error('Logout API error:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('showApprovalPending'); // Clear approval pending flag
            // Note: Axios interceptor will automatically handle token removal
            commit('LOGOUT');
        }
    },

    async reportCompanySwitch({ state }, { companyId, previousCompanyId }) {
        try {
            const token = state.token || localStorage.getItem('token');
            if (!token || !companyId || !previousCompanyId || companyId === previousCompanyId) return;
            await axios.post('/client/record-company-context', {
                companyId,
                previousCompanyId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            logger.error('reportCompanySwitch error:', error);
        }
    },

    completeInviteLogin({ commit }, { token, client }) {
        if (!token || !client) {
            throw new Error('Invite session incomplete.');
        }
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(client));
        commit('SET_AUTH_SUCCESS', { token, user: client });
    },

    async verifyToken({ commit }) {
        // Simple check: token exists in localStorage
        const token = localStorage.getItem('token');
        if (!token) {
            commit('LOGOUT');
            throw new Error('No token');
        }
        // Optionally, decode and check expiry here
        return true;
    }
};

const mutations = {
    SET_AUTH_SUCCESS(state, { token, user }) {
        state.token = token;
        state.user = user;
        state.isLoggedIn = true;
    },

    UPDATE_USER(state, user) {
        state.user = user;
    },

    LOGOUT(state) {
        state.token = null;
        state.user = null;
        state.isLoggedIn = false;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}; 